'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {StrapiNews} from '@/types/strapi';
import {useLocale} from 'next-intl';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';
import {useEffect, useState} from 'react';
import DateBlock from '../tools/DateBlock';
import {LazyImage} from '../tools/LazyImage';
import ComingSoon from './ComingSoon';
import {formDummyRows, LoadingItem} from './LoadingItem';
import Pagination from './Pagination';

export default function SustainabilityList(prop: {sustainability: string}) {
  const locale = useLocale();
  const [searchResults, setSearchResults] = useState<StrapiNews[]>([]);
  const searchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    return parseInt(searchParams.get('p') || '1');
  });
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const getSearchResult = async () => {
    setIsLoading(true);
    const postData = await getStrapiData(locale, 'posts', {
      filters: {
        post_type: 'sustainability',
        sustainability: {
          $contains: prop.sustainability
        },
        posted_at: {
          $lte: new Date()
        }
      },
      populate: 'thumbnail',
      sort: 'posted_at:desc',
      pagination: {
        page: currentPage,
        pageSize: 10
      }
    });

    const totalPages = postData?.meta?.pagination?.pageCount || 1;
    setSearchResults(postData?.data || []);
    setTotalPages(totalPages);
    setIsLoading(false);
  };

  useEffect(() => {
    getSearchResult();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('p', currentPage.toString());
    window.history.pushState({}, '', url.href);
    getSearchResult();
  }, [currentPage]);

  return (
    <div className="questrial text-[18px]" id="sustainability-list">
      <div>
        {!isLoading && searchResults.length === 0 && <ComingSoon />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4  mt-4">
          {isLoading && (
            <LoadingItem>
              <div className="w-full">
                <div className="w-full">
                  <div className="mb-4 w-full">
                    <LazyImage className="aspect-[3/2] w-full" />
                  </div>
                  <div>
                    <div className="h-[16px] bg-gray-100 rounded-full w-1/2 mb-2"></div>
                    <div className="h-[24px] bg-gray-100 rounded-full w-full mb-2"></div>
                  </div>
                  <div className="w-full flex flex-col gap-2 text-xs">
                    {formDummyRows(3)}
                  </div>
                </div>
              </div>
            </LoadingItem>
          )}
          {!isLoading &&
            searchResults.map((result: StrapiNews, index) => (
              <div
                key={index}
                className=" hover:bg-gray-100 border border-[#fff] hover:border-gray-100 "
              >
                <Link
                  prefetch={false}
                  href={'/sustainability-story/' + result.slug}
                  className="fade-in-up"
                >
                  <div className="flex flex-col  ">
                    <div className="mb-4 md:flex md:flex-cols-2 gap-2 md:items-start">
                      <LazyImage
                        src={result.thumbnail?.url}
                        className="aspect-[3/2] w-full"
                      />
                    </div>
                    <div className="px-2 h-40">
                      <div className="text-[12px]">
                        <DateBlock date={result.posted_at} />
                      </div>
                      <h2
                        className={cn(
                          'text-[24px] text-[#e31836] mb-4 overflow-hidden',
                          locale == 'en' ? 'font-[700]' : 'font-[500]'
                        )}
                      >
                        <div className="line-clamp-3">{result.title}</div>
                      </h2>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
        </div>
        {!isLoading && searchResults.length > 0 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            paginationId="sustainability-list"
          />
        )}
      </div>
    </div>
  );
}
